import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useRoles } from '../../hooks/useRoles';
import apiService from '../../services/apiService';
import { useToast } from '../../hooks/useToast';
import { useConversationRefresh } from '../../hooks/useConversationRefresh';
import '../../styles/conversations.css';
import '../../styles/activityReporting.css';
import { Briefcase, UsersThree, Archive, ArrowCounterClockwise } from '@phosphor-icons/react';

// Anything other than the two known methodologies renders as blank, not an error.
const METHODOLOGY_LABELS = {
  real: 'Roleplay',
  basic: 'Case Study',
  simple: 'Discussion',
};

const formatMethodologyLabel = (m) => METHODOLOGY_LABELS[(m || '').toLowerCase()] || '';

// Display names for 'simple' conversation flavors (metadata.flavor).
// Unknown flavors fall back to titleCase of the raw value.
const FLAVOR_LABELS = {
  cohort_real_summary: 'Cohort Summary',
  cohort_themes: 'Cohort Themes',
};

// Icons for known conversation types. A type without an entry here still gets
// a filter button — just without an icon. `type` itself is free-form
// (metadata.type has no DB or backend constraint), so this map is decoration
// only, never a gate on which types are filterable.
const TYPE_ICONS = {
  sales: Briefcase,
  leadership: UsersThree,
};

const titleCase = (s) => {
  if (!s) return '—';
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

// Second half of the Type cell: the flavor for 'simple' conversations
// (defaulting to 'Discussion'), otherwise the methodology label.
const formatSecondaryLabel = (c) => {
  if ((c.methodology || '').toLowerCase() === 'simple') {
    if (!c.flavor) return 'Discussion';
    return FLAVOR_LABELS[c.flavor] || titleCase(c.flavor);
  }
  return formatMethodologyLabel(c.methodology);
};

const ConversationsPage = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isSuperAdmin, isAdmin } = useRoles();
  const { triggerRefresh } = useConversationRefresh();

  // RPT-003 has no plain-Admin access, even to one's own conversations — mirrors
  // canDrillToDebrief in UserConversationListPage.js (My Activity).
  const canDrillToDebrief = isSuperAdmin() || !isAdmin();

  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [sortBy, setSortBy] = useState('updated');
  const [sortOrder, setSortOrder] = useState('desc');

  // Load all conversations
  useEffect(() => {
    const loadConversations = async () => {
      try {
        setLoading(true);
        setError(null);

        const url = '/api/v1/conversations?include_archived=true';
        const data = await apiService.get(url, user);

        let conversationsArray = [];

        if (data && data.success && data.data && Array.isArray(data.data.conversations)) {
          conversationsArray = data.data.conversations;
        } else {
          console.warn('Unexpected conversations response structure:', data);
          conversationsArray = [];
        }

        setConversations(conversationsArray);
      } catch (err) {
        console.error('Error loading conversations:', err);
        setError('Failed to load conversations');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadConversations();
    }
  }, [user]);

  // Filter and sort conversations
  useEffect(() => {
    let result = [...conversations];

    // Auto-exit archive view if there are no archived conversations left
    if (showArchived && result.filter(conv => conv.status === 'archived').length === 0) {
      setShowArchived(false);
      return;
    }

    // Apply archive filter
    if (!showArchived) {
      result = result.filter(conv => conv.status !== 'archived');
    } else {
      result = result.filter(conv => conv.status === 'archived');
    }

    // Apply type filter
    if (typeFilter !== 'all') {
      result = result.filter(conv => {
        const convType = (conv.type || '').toLowerCase();
        return convType === typeFilter.toLowerCase();
      });
    }

    // Apply search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(conv =>
        conv.title?.toLowerCase().includes(term)
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      let aVal, bVal;

      switch (sortBy) {
        case 'title':
          aVal = (a.title || '').toLowerCase();
          bVal = (b.title || '').toLowerCase();
          break;
        case 'created':
          aVal = new Date(a.createdAt || 0);
          bVal = new Date(b.createdAt || 0);
          break;
        case 'updated':
        default:
          aVal = new Date(a.lastActivity || a.createdAt || 0);
          bVal = new Date(b.lastActivity || b.createdAt || 0);
          break;
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredConversations(result);
  }, [conversations, searchTerm, typeFilter, showArchived, sortBy, sortOrder]);

  const handleConversationClick = (conversationId, isArchived) => {
    // Don't allow opening archived conversations
    if (isArchived) {
      return;
    }
    navigate(`/app/chat/${conversationId}`);
  };

  const handleDebriefClick = (e, conversationId, conversationTitle) => {
    e.stopPropagation(); // Don't also trigger the row's resume-chat navigation
    navigate(`/app/my-activity/conversations/${conversationId}`, {
      state: { conversationTitle, backTo: '/app/conversations', backLabel: 'Conversations' }
    });
  };

  const handleArchiveToggle = async (e, conversationId, currentStatus) => {
    e.stopPropagation(); // Prevent navigation

    try {
      const isArchived = currentStatus === 'archived';
      const endpoint = isArchived
        ? `/api/v1/conversations/${conversationId}/unarchive`
        : `/api/v1/conversations/${conversationId}/archive`;

      await apiService.patch(endpoint, {}, user);

      // Update local state
      const newStatus = isArchived ? 'active' : 'archived';
      setConversations(prev =>
        prev.map(conv =>
          conv.id === conversationId
            ? { ...conv, status: newStatus }
            : conv
        )
      );

      triggerRefresh(); // ← update conversation list immediately

      showToast(
        isArchived ? 'Conversation unarchived' : 'Conversation archived',
        'success'
      );
    } catch (err) {
      console.error('Error toggling archive status:', err);
      showToast('Failed to update conversation status', 'error');
    }
  };

  const toggleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }

    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const getTypeLabel = (type) => {
    if (type === 'sales') return 'Sales';
    return type ? type.charAt(0).toUpperCase() + type.slice(1) : '';
  };

  // Distinct conversation types actually present in the data, so the filter
  // row never hides a type or shows one that doesn't exist.
  const conversationTypes = Array.from(
    new Set(conversations.map((c) => (c.type || '').toLowerCase()).filter(Boolean))
  ).sort();

  // Count conversations
  const activeCount = conversations.filter(c => c.status !== 'archived').length;
  const archivedCount = conversations.filter(c => c.status === 'archived').length;
  const hasArchivedConversations = archivedCount > 0;

  if (error) {
    return (
      <div className="conversation-list-view error">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => navigate('/app')}>Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="conversation-list-view">
      <div className="list-header">
        <h1>{showArchived ? 'Archived Conversations' : 'My Active Conversations'}</h1>
        {!loading && (
          <p className="conversation-count">
            {filteredConversations.length} of {showArchived ? archivedCount : activeCount} conversations
          </p>
        )}
      </div>

      <div className="list-controls">
        {/* Search */}
        <div className="search-box">
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button
              className="clear-search"
              onClick={() => setSearchTerm('')}
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>

        {/* Type Filter */}
        <div className="type-filter">
          <button
            className={`filter-btn ${typeFilter === 'all' ? 'active' : ''}`}
            onClick={() => setTypeFilter('all')}
          >
            All
          </button>
          {conversationTypes.map((type) => {
            const Icon = TYPE_ICONS[type];
            return (
              <button
                key={type}
                className={`filter-btn ${typeFilter === type ? 'active' : ''}`}
                onClick={() => setTypeFilter(type)}
              >
                {Icon && <Icon weight="light" size={14} />} {getTypeLabel(type)}
              </button>
            );
          })}
          {hasArchivedConversations && (
            <button
              className={`filter-btn ${showArchived ? 'active' : ''}`}
              onClick={() => setShowArchived(!showArchived)}
            >
              <Archive weight="light" size={14} /> Archived
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="arp-state">Loading…</div>
      ) : filteredConversations.length === 0 ? (
        <div className="arp-state">
          {searchTerm || typeFilter !== 'all' ? (
            'No conversations match your filters'
          ) : showArchived ? (
            'No archived conversations'
          ) : (
            'No conversations yet. Start a new conversation!'
          )}
        </div>
      ) : (
        <div className="arp-table-wrapper">
          <table className="arp-table">
            <thead>
              <tr>
                <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('title')}>
                  Conversation {sortBy === 'title' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th>Type</th>
                <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('updated')}>
                  Last Activity {sortBy === 'updated' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th>Phase</th>
                {canDrillToDebrief && <th>Debrief</th>}
                <th>Archive</th>
              </tr>
            </thead>
            <tbody>
              {filteredConversations.map((c) => {
                const isArchived = c.status === 'archived';
                return (
                  <tr
                    key={c.id}
                    className={isArchived ? 'archived not-clickable' : ''}
                    onClick={() => handleConversationClick(c.id, isArchived)}
                  >
                    <td>
                      {isArchived ? (
                        c.title || 'Untitled Conversation'
                      ) : (
                        <button className="arp-row-link" onClick={() => handleConversationClick(c.id, isArchived)}>
                          {c.title || 'Untitled Conversation'}
                        </button>
                      )}
                    </td>
                    <td className="arp-cell-muted">
                      {getTypeLabel((c.type || '').toLowerCase())}{formatSecondaryLabel(c) ? ` · ${formatSecondaryLabel(c)}` : ''}
                    </td>
                    <td className="arp-cell-muted">{formatDate(c.lastActivity)}</td>
                    <td className="arp-cell-muted">
                      {titleCase(c.currentPhase)}{c.currentSubphase ? ` · ${titleCase(c.currentSubphase)}` : ''}
                    </td>
                    {canDrillToDebrief && (
                      <td>
                        <button className="arp-row-link" onClick={(e) => handleDebriefClick(e, c.id, c.title)}>
                          View debrief
                        </button>
                      </td>
                    )}
                    <td>
                      <button
                        className="action-btn archive-btn"
                        onClick={(e) => handleArchiveToggle(e, c.id, c.status)}
                        title={isArchived ? 'Unarchive' : 'Archive'}
                      >
                        {isArchived
                          ? <><ArrowCounterClockwise weight="light" size={14} /> Unarchive</>
                          : <><Archive weight="light" size={14} /> Archive</>
                        }
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ConversationsPage;
